import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ManageBank.scss";
import ErrorMessage, {
  limitMsg,
} from "../../../Components/Common/ErrorMessage/ErrorMessage";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import Button from "../../../Components/Common/Button/Button";
import Input from "../../../Components/Common/Input/Input";
import useDebounce from "../../../hooks/useDebounce";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import Table, { type Column } from "../../../Components/Table/Table";
import Offcanvas from "../../../Components/Common/Offcanvas/Offcanvas";
import NetworkError from "Components/Common/NetworkError/NetworkError";
import { useToast } from "Components/Common/Toast/ToastContext";
import { get, post, patch } from "../../../utils/api/apiCaller";
import { ENDPOINTS } from "../../../utils/api/endpoints";
import PlusIcon from "Assets/Img/PlusIcon";
import EmptyState from "Components/Common/EmptyState";
import EmptyChangeOrderIcon from "Assets/Img/EmptyChangeOrderIcon";
import BankRowSkeleton from "Components/Common/Skeleton/BankRowSkeleton";

// ─── Types ────────────────────────────────────────────────────────────────────

type DrawerMode = "new" | "view" | "addBranch";

// Set by Add New Project step 4 ("Add new bank / branch"): open the matching drawer and
// return to the project form afterwards, with the bank/branch to select.
interface ManageBankForState {
  manageBankFor?: {
    mode: "bank" | "branch";
    bankId?: string;
    returnTo: string;
  };
}
type FormErrorKey = "name" | "address";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Bank {
  id: string;
  name: string;
  address: string;
  branchCount: number;
  allBranches: Branch[];
}

interface BankTableRow {
  key: string;
  bankId: string;
  bankName: string;
  branches: { id: string; branchName: string }[];
}

interface BankApiResponse {
  id: string;
  name: string;
  companyType: string;
  address: string;
  branchCount: string;
}

interface Branch {
  id: string;
  branchName: string;
  companyName: string;
  companyId?: string;
  address: string;
  projects: { id: string; name: string }[];
}

interface BranchFormRow {
  id?: string;
  branchName: string;
  branchAddress: string;
}

interface PaginationData {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
}

interface FormState {
  name: string;
  address: string;
}

type FormErrors = Partial<Record<FormErrorKey, string>>;
type TouchedFields = Partial<Record<FormErrorKey, boolean>>;
type BranchFormErrors = { branchName?: string; branchAddress?: string };
type BranchTouched = { branchName?: boolean; branchAddress?: boolean };

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_LIMIT = 10;
const EMPTY_FORM: FormState = { name: "", address: "" };
const EMPTY_BRANCH: BranchFormRow = { branchName: "", branchAddress: "" };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mapRawToBank(raw: BankApiResponse): Bank {
  return {
    id: raw.id,
    name: raw.name,
    address: raw.address,
    branchCount: parseInt(raw.branchCount, 10) || 0,
    allBranches: [],
  };
}

function formatBranchNames(branches: { branchName: string }[]): {
  preview: string;
  remaining: number;
} {
  return {
    preview: branches
      .slice(0, 2)
      .map((b) => b.branchName)
      .join(", "),
    remaining: Math.max(0, branches.length - 2),
  };
}

function getBankInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

async function fetchBranchesForBank(companyId: string): Promise<Branch[]> {
  const { data, error: apiError } = (await get(
    ENDPOINTS.COMPANIES.BRANCHES_LIST,
    { page: 1, limit: 100, companyId },
  )) as {
    data: { data?: Branch[] } | null;
    error: { message?: string } | null;
  };
  if (apiError || !data) return [];
  return data.data ?? [];
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ManageBank(): React.ReactElement {
  const showToast = useToast();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const returnFor = (routeLocation.state as ManageBankForState | null)
    ?.manageBankFor;
  const openedForReturnRef = useRef(false);

  // ── List state ──────────────────────────────────────────────────────────────
  const [allBanks, setAllBanks] = useState<Bank[]>([]);
  const [tableRows, setTableRows] = useState<BankTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: PAGE_LIMIT,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [currentSearch, setCurrentSearch] = useState("");
  const debouncedSearch = useDebounce(currentSearch, 500);

  // ── Drawer state ─────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("new");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null);
  const [branchUpdateLoading, setBranchUpdateLoading] = useState(false);

  // ── Create-flow wizard state ──────────────────────────────────────────────────
  const [bankCreated, setBankCreated] = useState(false);
  const [createdBankId, setCreatedBankId] = useState<string | null>(null);

  // ── Form state ───────────────────────────────────────────────────────────────
  const [formState, setFormState] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});

  // ── Branch form state ─────────────────────────────────────────────────────────
  const [branches, setBranches] = useState<BranchFormRow[]>([
    { ...EMPTY_BRANCH },
  ]);
  const [branchErrors, setBranchErrors] = useState<BranchFormErrors[]>([{}]);
  const [branchTouched, setBranchTouched] = useState<BranchTouched[]>([{}]);

  // ── Refs ──────────────────────────────────────────────────────────────────────
  const nameRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);
  const branchRowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevBranchLengthRef = useRef(0);
  const userAddedBranchRef = useRef(false);
  const originalBranchRef = useRef<BranchFormRow | null>(null);

  // ── Effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    if (bankCreated) {
      const firstRow = branchRowRefs.current[0];
      firstRow?.querySelector<HTMLInputElement>("input")?.focus();
    }
  }, [bankCreated]);

  useEffect(() => {
    if (
      branches.length > prevBranchLengthRef.current &&
      userAddedBranchRef.current
    ) {
      branchRowRefs.current[branches.length - 1]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      userAddedBranchRef.current = false;
    }
    prevBranchLengthRef.current = branches.length;
  }, [branches.length]);

  // ── API ───────────────────────────────────────────────────────────────────────

  const fetchAllBanks = useCallback(async (): Promise<void> => {
    const { data } = (await get(ENDPOINTS.COMPANIES.LIST, {
      page: 1,
      limit: 100,
    })) as { data: { data?: BankApiResponse[] } | null; error: unknown };
    const list = (data?.data ?? [])
      .filter((c) => c.companyType === "bank")
      .map(mapRawToBank);
    setAllBanks(list);
  }, []);

  const fetchTableRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError("");
    const params: Record<string, unknown> = { page, limit: PAGE_LIMIT };
    if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
    if (currentFilter !== "all") params.companyId = currentFilter;

    const { data, error: apiError } = (await get(
      ENDPOINTS.COMPANIES.BRANCHES_LIST,
      params,
    )) as {
      data: {
        data?: Branch[];
        pagination?: {
          page: number;
          totalPages: number;
          total: number;
          limit: number;
        };
      } | null;
      error: { message?: string } | null;
    };

    setLoading(false);
    if (apiError || !data) {
      setError(apiError?.message ?? "Failed to load branches.");
      return;
    }

    const raw = data.data ?? [];

    const bankMap = new Map<string, BankTableRow>();
    for (const br of raw) {
      const bankId =
        br.companyId ?? (currentFilter !== "all" ? currentFilter : "");
      const groupKey = bankId || br.companyName;
      if (!bankMap.has(groupKey)) {
        bankMap.set(groupKey, {
          key: groupKey,
          bankId,
          bankName: br.companyName,
          branches: [],
        });
      }
      bankMap
        .get(groupKey)!
        .branches.push({ id: br.id, branchName: br.branchName });
    }
    setTableRows([...bankMap.values()]);

    setPagination({
      page: data.pagination?.page ?? page,
      totalPages: data.pagination?.totalPages ?? 1,
      total: data.pagination?.total ?? raw.length,
      limit: data.pagination?.limit ?? PAGE_LIMIT,
    });
  }, [page, debouncedSearch, currentFilter]);

  useEffect(() => {
    fetchAllBanks();
  }, [fetchAllBanks]);

  useEffect(() => {
    fetchTableRows();
  }, [fetchTableRows]);

  useEffect(() => {
    setPage(1);
  }, [currentFilter]);

  const fetchBranches = (companyId: string): Promise<Branch[]> =>
    fetchBranchesForBank(companyId);

  const createBank = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const { error: apiError } = (await post(ENDPOINTS.COMPANIES.LIST, {
      name: formState.name.trim(),
      companyType: "bank",
      address: formState.address.trim(),
    })) as { data: unknown; error: { message?: string } | null };
    if (apiError) return { success: false, error: apiError.message };
    return { success: true };
  };

  const createBranch = async (
    companyId: string,
    branch: BranchFormRow,
    companyName: string = "",
  ): Promise<{ success: boolean; error?: string }> => {
    const { error: apiError } = (await post(
      ENDPOINTS.COMPANIES.BRANCH_CREATE(companyId),
      {
        branchName: branch.branchName.trim(),
        address: branch.branchAddress.trim(),
        companyName,
      },
    )) as { data: unknown; error: { message?: string } | null };
    if (apiError) return { success: false, error: apiError.message };
    return { success: true };
  };

  const updateBranch = async (
    companyId: string,
    branchId: string,
    branch: BranchFormRow,
  ): Promise<{ success: boolean; error?: string }> => {
    const { error: apiError } = (await patch(
      ENDPOINTS.COMPANIES.BRANCH_DETAIL(companyId, branchId),
      {
        branchName: branch.branchName.trim(),
        address: branch.branchAddress.trim(),
      },
    )) as { data: unknown; error: { message?: string } | null };
    if (apiError) return { success: false, error: apiError.message };
    return { success: true };
  };

  // ── Validation ────────────────────────────────────────────────────────────────

  const validateBank = (state: FormState): FormErrors => {
    const errors: FormErrors = {};
    if (!state.name.trim()) errors.name = "Bank name is required";
    else if (state.name.length > CHAR_LIMIT.NAME)
      errors.name = limitMsg(CHAR_LIMIT.NAME);
    if (!state.address.trim()) errors.address = "Bank address is required";
    else if (state.address.length > CHAR_LIMIT.ADDRESS)
      errors.address = limitMsg(CHAR_LIMIT.ADDRESS);
    return errors;
  };

  const validateBranches = (rows: BranchFormRow[]): BranchFormErrors[] =>
    rows.map((b) => {
      const err: BranchFormErrors = {};
      if (!b.branchName.trim()) err.branchName = "Branch name is required";
      else if (b.branchName.length > CHAR_LIMIT.NAME)
        err.branchName = limitMsg(CHAR_LIMIT.NAME);
      if (!b.branchAddress.trim())
        err.branchAddress = "Branch address is required";
      else if (b.branchAddress.length > CHAR_LIMIT.ADDRESS)
        err.branchAddress = limitMsg(CHAR_LIMIT.ADDRESS);
      return err;
    });

  const scrollToFirstError = (errors: FormErrors): void => {
    if (errors.name && nameRef.current) {
      nameRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      nameRef.current.querySelector<HTMLElement>("input")?.focus();
    } else if (errors.address && addressRef.current) {
      addressRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      addressRef.current.querySelector<HTMLElement>("input")?.focus();
    }
  };

  const handleBlur = (field: FormErrorKey): void => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFormErrors(validateBank(formState));
  };

  const handleFieldChange = (field: keyof FormState, value: string): void => {
    const next = { ...formState, [field]: value };
    setFormState(next);
    if (touched[field as FormErrorKey]) setFormErrors(validateBank(next));
  };

  // ── Branch handlers (new bank flow) ──────────────────────────────────────────

  const addBranch = (): void => {
    userAddedBranchRef.current = true;
    setBranches((prev) => [...prev, { ...EMPTY_BRANCH }]);
    setBranchErrors((prev) => [...prev, {}]);
    setBranchTouched((prev) => [...prev, {}]);
  };

  const removeBranch = (idx: number): void => {
    setBranches((prev) => prev.filter((_, i) => i !== idx));
    setBranchErrors((prev) => prev.filter((_, i) => i !== idx));
    setBranchTouched((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBranchChange = (
    idx: number,
    field: "branchName" | "branchAddress",
    value: string,
  ): void => {
    const next = branches.map((b, i) =>
      i === idx ? { ...b, [field]: value } : b,
    );
    setBranches(next);
    if (branchTouched[idx]?.[field]) setBranchErrors(validateBranches(next));
  };

  const handleBranchBlur = (idx: number, field: keyof BranchFormRow): void => {
    setBranchTouched((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: true };
      return next;
    });
    setBranchErrors(validateBranches(branches));
  };

  // ── Branch accordion handlers (view mode) ────────────────────────────────────

  const openBranchEdit = (branchId: string, idx: number): void => {
    originalBranchRef.current = { ...branches[idx] };
    setEditingBranchId(branchId);
  };

  const cancelBranchEdit = (idx: number): void => {
    const original = originalBranchRef.current;
    originalBranchRef.current = null;
    if (original) {
      setBranches((prev) => {
        const next = [...prev];
        next[idx] = { ...original };
        return next;
      });
    }
    setEditingBranchId(null);
  };

  const handleUpdateBranch = async (idx: number): Promise<void> => {
    const branch = branches[idx];
    if (!editingId || !branch.id) return;

    setBranchTouched((prev) => {
      const next = [...prev];
      next[idx] = { branchName: true, branchAddress: true };
      return next;
    });
    const errs = validateBranches([branch])[0];
    setBranchErrors((prev) => {
      const next = [...prev];
      next[idx] = errs;
      return next;
    });
    if (Object.keys(errs).length > 0) return;

    setBranchUpdateLoading(true);
    const { success, error: updateError } = await updateBranch(
      editingId,
      branch.id,
      branch,
    );
    setBranchUpdateLoading(false);

    if (!success) {
      showToast(`⚠ ${updateError ?? "Failed to update branch."}`);
      return;
    }

    showToast(`✓ Branch updated successfully`);
    setEditingBranchId(null);
    originalBranchRef.current = null;

    setBranchesLoading(true);
    const [fetched] = await Promise.all([
      fetchBranches(editingId),
      fetchTableRows(),
    ]);
    setBranchesLoading(false);
    resetBranchState(
      fetched.map((b) => ({
        id: b.id,
        branchName: b.branchName,
        branchAddress: b.address,
      })),
    );
  };

  // ── Add Branch handler (addBranch mode) ──────────────────────────────────────

  const handleAddBranch = async (): Promise<void> => {
    const branch = branches[0];
    setBranchTouched([{ branchName: true, branchAddress: true }]);
    const errs = validateBranches([branch])[0];
    setBranchErrors([errs]);
    if (Object.keys(errs).length > 0) return;

    if (!editingId) return;

    setSaveLoading(true);
    const { success, error: createError } = await createBranch(
      editingId,
      branch,
      drawerBank?.name ?? "",
    );
    setSaveLoading(false);

    if (!success) {
      showToast(`⚠ ${createError ?? "Failed to add branch."}`);
      return;
    }

    showToast(`✓ Branch added successfully`);
    if (returnFor) {
      const added = (await fetchBranchesForBank(editingId)).find(
        (b) => b.branchName === branch.branchName.trim(),
      );
      resetDrawer();
      navigate(returnFor.returnTo, {
        replace: true,
        state: {
          createdBank: {
            bankId: editingId,
            bankName: drawerBank?.name ?? "",
            branch: added
              ? { id: added.id, name: added.branchName }
              : undefined,
          },
        },
      });
      return;
    }
    closeDrawer();
    await Promise.all([fetchAllBanks(), fetchTableRows()]);
  };

  // ── Drawer ────────────────────────────────────────────────────────────────────

  const drawerBank: Bank | null =
    editingId != null
      ? (allBanks.find((b) => b.id === editingId) ?? null)
      : null;

  const resetBranchState = (rows: BranchFormRow[]): void => {
    setBranches(rows);
    setBranchErrors(rows.map(() => ({})));
    setBranchTouched(rows.map(() => ({})));
  };

  const openDrawer = async (
    mode: DrawerMode,
    bankId: string | null = null,
  ): Promise<void> => {
    setDrawerMode(mode);
    setDrawerOpen(true);
    setFormErrors({});
    setTouched({});
    setEditingBranchId(null);
    originalBranchRef.current = null;

    if ((mode === "view" || mode === "addBranch") && bankId != null) {
      const bank = allBanks.find((b) => b.id === bankId);
      if (!bank) return;
      setEditingId(bankId);
      resetBranchState([{ ...EMPTY_BRANCH }]);

      if (mode === "view") {
        setBranchesLoading(true);
        const fetched = await fetchBranches(bankId);
        setBranchesLoading(false);
        resetBranchState(
          fetched.map((b) => ({
            id: b.id,
            branchName: b.branchName,
            branchAddress: b.address,
          })),
        );
      }
    } else {
      setEditingId(null);
      setFormState(EMPTY_FORM);
      setBankCreated(false);
      setCreatedBankId(null);
      resetBranchState([{ ...EMPTY_BRANCH }]);
    }
  };

  const resetDrawer = (): void => {
    setDrawerOpen(false);
    setEditingId(null);
    setBankCreated(false);
    setCreatedBankId(null);
    setEditingBranchId(null);
    originalBranchRef.current = null;
    // branches are reset in openDrawer before data loads, not here,
    // so the close animation doesn't flash stale or empty state
  };

  const closeDrawer = (): void => {
    resetDrawer();
    // Cancelled while adding a bank/branch for a project: go back to the project form.
    if (returnFor && drawerMode !== "view") {
      navigate(returnFor.returnTo, { replace: true });
    }
  };

  // ── Step 1: Create Bank ───────────────────────────────────────────────────────
  const handleCreateBank = async (): Promise<void> => {
    setTouched({ name: true, address: true });
    const bankErrs = validateBank(formState);
    setFormErrors(bankErrs);
    if (Object.keys(bankErrs).length > 0) {
      scrollToFirstError(bankErrs);
      return;
    }

    setSaveLoading(true);

    const existingIds = new Set(allBanks.map((b) => b.id));
    const { success: bankOk, error: createError } = await createBank();
    if (!bankOk) {
      setSaveLoading(false);
      showToast(`⚠ ${createError ?? "Failed to create bank."}`);
      return;
    }

    const { data: freshData } = (await get(ENDPOINTS.COMPANIES.LIST, {
      page: 1,
      limit: 100,
    })) as { data: { data?: BankApiResponse[] } | null; error: unknown };

    const newBankEntry = (freshData?.data ?? [])
      .filter((c) => c.companyType === "bank" && !existingIds.has(c.id))
      .at(0);

    setSaveLoading(false);

    if (!newBankEntry) {
      showToast(`⚠ Bank created but could not retrieve ID. Please refresh.`);
      return;
    }

    setCreatedBankId(newBankEntry.id);
    setBankCreated(true);
    fetchAllBanks();
  };

  // ── Step 2: Save Branches ─────────────────────────────────────────────────────
  const handleSaveBranches = async (): Promise<void> => {
    setBranchTouched(
      branches.map(() => ({ branchName: true, branchAddress: true })),
    );
    const branchErrs = validateBranches(branches);
    setBranchErrors(branchErrs);

    if (branchErrs.some((e) => Object.keys(e).length > 0)) {
      const firstErrIdx = branchErrs.findIndex(
        (e) => Object.keys(e).length > 0,
      );
      branchRowRefs.current[firstErrIdx]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (!createdBankId) return;

    setSaveLoading(true);

    let createdCount = 0;
    for (const branch of branches) {
      const { success: branchOk, error: branchError } = await createBranch(
        createdBankId,
        branch,
        formState.name.trim(),
      );
      if (!branchOk) {
        showToast(
          `⚠ Branch "${branch.branchName || `#${branches.indexOf(branch) + 1}`}" failed: ${branchError ?? "Unknown error"}`,
        );
      } else {
        createdCount++;
      }
    }

    if (createdCount === branches.length) {
      showToast(`✓ ${formState.name} created successfully`);
    } else if (createdCount > 0) {
      showToast(
        `✓ ${formState.name} created (${createdCount}/${branches.length} branches added)`,
      );
    }

    setSaveLoading(false);
    if (returnFor) {
      const created = await fetchBranchesForBank(createdBankId);
      resetDrawer();
      navigate(returnFor.returnTo, {
        replace: true,
        state: {
          createdBank: {
            bankId: createdBankId,
            bankName: formState.name.trim(),
            // Only pre-select the branch when the bank has exactly one.
            branch:
              created.length === 1
                ? { id: created[0].id, name: created[0].branchName }
                : undefined,
          },
        },
      });
      return;
    }
    closeDrawer();
    await Promise.all([fetchAllBanks(), fetchTableRows()]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Opened from Add New Project: open the new-bank / add-branch drawer straight away.
  useEffect(() => {
    if (!returnFor || openedForReturnRef.current) return;
    if (returnFor.mode === "bank") {
      openedForReturnRef.current = true;
      openDrawer("new");
      return;
    }
    if (!allBanks.length) return;
    openedForReturnRef.current = true;
    openDrawer("addBranch", returnFor.bankId ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBanks]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="manageBankPage">
      <div className="page">
        {/* Header */}
        <div className="pageHeader">
          <div>
            <div className="pageTitle">Manage Banks</div>
            <div className="pageSub">
              Add, edit, and manage bank. Only CSC Admins can manage banks.
            </div>
          </div>
          <Button
            className="btn btnPrimary"
            type="button"
            onClick={() => openDrawer("new")}
          >
            <PlusIcon />
            Add Bank
          </Button>
        </div>

        {/* Bank filter chips */}
        <div className="statusStats">
          <div
            className={`statusChip statusChip--all${currentFilter === "all" ? " statusChipActive" : ""}`}
            onClick={() => setCurrentFilter("all")}
          >
            <span className="scLabel">All Banks</span>
            <span className="scCount">{allBanks.length}</span>
          </div>
          {allBanks.map((bank) => (
            <div
              key={bank.id}
              className={`statusChip statusChip--bank${currentFilter === bank.id ? " statusChipActive" : ""}`}
              onClick={() => setCurrentFilter(bank.id)}
            >
              <div className="scDot" />
              <span className="scLabel">{bank.name}</span>
              <span className="scCount">{bank.branchCount}</span>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <div className="searchWrap">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle
                cx="6"
                cy="6"
                r="4.5"
                stroke="var(--tx3)"
                strokeWidth="1.3"
              />
              <line
                x1="9.5"
                y1="9.5"
                x2="13"
                y2="13"
                stroke="var(--tx3)"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            <Input
              className="searchInput"
              type="text"
              name="bankSearch"
              autoComplete="off"
              id="bankSearch"
              placeholder="Search banks by name or address…"
              value={currentSearch}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCurrentSearch(e.target.value)
              }
              label={undefined}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div
            className="plist table-responsive"
            style={{
              borderRadius: 10,
              overflow: "hidden",
              border: "0.5px solid var(--bd)",
            }}
          >
            <table className="table">
              <thead>
                <tr className="chdr">
                  <th>Bank</th>
                  <th>Branches</th>
                  <th>Address</th>
                  <th className="colAction" />
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <BankRowSkeleton key={i} />
                ))}
              </tbody>
            </table>
          </div>
        ) : error ? (
          <NetworkError />
        ) : (
          <Table<BankTableRow>
            data={tableRows}
            fallbackUI={
              <EmptyState
                noBorder
                icon={<EmptyChangeOrderIcon />}
                title="No Banks Available"
                subtitle="No banks match your current filters."
              />
            }
            columns={
              [
                {
                  header: "Bank",
                  render: (row) => (
                    <div className="bankInfo">
                      <div className="bankAvatar">
                        {getBankInitials(row.bankName)}
                      </div>
                      <div className="bankName">{row.bankName}</div>
                    </div>
                  ),
                },
                {
                  header: "Branches",
                  render: (row) =>
                    (() => {
                      if (row.branches.length === 0) return <span>—</span>;
                      const { preview, remaining } = formatBranchNames(
                        row.branches,
                      );
                      return (
                        <span className="branchNameCell">
                          {preview}
                          {remaining > 0 && (
                            <span className="branchCountBadge">
                              +{remaining}
                            </span>
                          )}
                        </span>
                      );
                    })(),
                },
                {
                  header: "Address",
                  render: (row) => {
                    const bank = allBanks.find(
                      (b) => b.id === row.bankId || b.name === row.bankName,
                    );
                    return <span>{bank?.address ?? "—"}</span>;
                  },
                },
                {
                  header: "",
                  className: "colAction",
                  render: (row) => {
                    const bankId =
                      row.bankId ||
                      allBanks.find((b) => b.name === row.bankName)?.id ||
                      "";
                    return (
                      <div className="rowActionGroup">
                        <Button
                          className="btnGhost"
                          type="button"
                          onClick={() => openDrawer("view", bankId)}
                          disabled={!bankId}
                        >
                          View / Edit
                        </Button>
                        <Button
                          className="btnGhost"
                          type="button"
                          onClick={() => openDrawer("addBranch", bankId)}
                          disabled={!bankId}
                        >
                          Add Branch
                        </Button>
                      </div>
                    );
                  },
                },
              ] as Column<BankTableRow>[]
            }
          />
        )}

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={(p: number) => setPage(p)}
        />
      </div>

      <Offcanvas
        isOpen={drawerOpen}
        onClose={bankCreated ? () => {} : closeDrawer}
        title={
          drawerMode === "view" && drawerBank
            ? `Edit Bank — ${drawerBank.name}`
            : drawerMode === "addBranch" && drawerBank
              ? `Add Branch — ${drawerBank.name}`
              : "Add New Bank"
        }
        position="end"
        width="520px"
        footer={
          drawerMode === "view" ? undefined : (
            <>
              <Button
                className="btn btnSecondary"
                type="button"
                onClick={closeDrawer}
                disabled={bankCreated}
              >
                Cancel
              </Button>
              <Button
                className="btn btnPrimary"
                type="button"
                onClick={
                  drawerMode === "addBranch"
                    ? handleAddBranch
                    : bankCreated
                      ? handleSaveBranches
                      : handleCreateBank
                }
                disabled={saveLoading || branchesLoading}
              >
                {saveLoading ? (
                  <>
                    <span className="btnSpinner" />{" "}
                    {drawerMode === "addBranch"
                      ? "Adding"
                      : bankCreated
                        ? "Saving"
                        : "Creating"}
                  </>
                ) : drawerMode === "addBranch" ? (
                  "Add Branch"
                ) : bankCreated ? (
                  "Save Branches"
                ) : (
                  "Create Bank"
                )}
              </Button>
            </>
          )
        }
      >
        {/* Profile preview (view / addBranch modes) */}
        {(drawerMode === "view" || drawerMode === "addBranch") &&
          drawerBank && (
            <div className="profilePreview">
              <div className="ppAvatar">{getBankInitials(drawerBank.name)}</div>
              <div className="ppInfo">
                <div>
                  <div className="ppName">{drawerBank.name}</div>
                  <div className="ppMeta">{drawerBank.address}</div>
                </div>
                <div className="ppMeta">
                  {drawerBank.branchCount} branch
                  {drawerBank.branchCount !== 1 ? "es" : ""}
                </div>
              </div>
            </div>
          )}

        {/* Bank Details — new flow only */}
        {drawerMode === "new" && (
          <div className="formSection">
            <div className="formSectionTitle">Bank Details</div>

            <div className="fieldRow" ref={nameRef}>
              <div className="field">
                <label>
                  Bank Name <span className="req">*</span>
                </label>
                <Input
                  type="text"
                  autoComplete="off"
                  name="bankName"
                  value={formState.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("name", e.target.value)
                  }
                  onBlur={() => handleBlur("name")}
                  className={
                    touched.name && formErrors.name ? "inputError" : ""
                  }
                  placeholder="e.g. First National Bank"
                  disabled={bankCreated}
                  maxLength={CHAR_LIMIT.NAME}
                  label={undefined}
                />
                <ErrorMessage
                  value={formState.name}
                  have={CHAR_LIMIT.NAME}
                  touched={touched.name}
                  error={formErrors.name}
                />
              </div>
            </div>

            <div className="fieldRow" ref={addressRef}>
              <div className="field">
                <label>
                  Bank Address <span className="req">*</span>
                </label>
                <Input
                  type="text"
                  autoComplete="off"
                  name="bankAddress"
                  value={formState.address}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange("address", e.target.value)
                  }
                  onBlur={() => handleBlur("address")}
                  className={
                    touched.address && formErrors.address ? "inputError" : ""
                  }
                  placeholder="e.g. 123 Main St, Portland, ME"
                  disabled={bankCreated}
                  maxLength={CHAR_LIMIT.ADDRESS}
                  label={undefined}
                />
                <ErrorMessage
                  value={formState.address}
                  have={CHAR_LIMIT.ADDRESS}
                  touched={touched.address}
                  error={formErrors.address}
                />
              </div>
            </div>
          </div>
        )}

        {/* Branch list — view mode (accordion) or new-bank step 2 */}
        {(drawerMode === "view" || bankCreated) && (
          <div className="formSection">
            <div className="formSectionTitle branchSectionTitle">
              <span>Branch Details</span>
              {bankCreated && (
                <Button
                  className="btn btnGhost"
                  type="button"
                  onClick={addBranch}
                  disabled={branchesLoading}
                >
                  + Add Branch
                </Button>
              )}
            </div>

            {branchesLoading ? (
              <div className="branchLoadingSkeleton">
                <div className="skCell skCell--wide" />
                <div className="skCell" />
              </div>
            ) : branches.length === 0 ? (
              <div className="branchEmptyNote">No branches yet.</div>
            ) : (
              branches.map((branch, idx) => (
                <div
                  key={branch.id ?? `new-${idx}`}
                  ref={(el) => {
                    branchRowRefs.current[idx] = el;
                  }}
                >
                  {/* Existing branch: accordion card */}
                  {branch.id ? (
                    <div className="branchCard">
                      <div
                        className="branchCardHeader"
                        onClick={() =>
                          editingBranchId === branch.id
                            ? cancelBranchEdit(idx)
                            : openBranchEdit(branch.id!, idx)
                        }
                      >
                        <span className="branchCardName">
                          {branch.branchName || "—"}
                        </span>
                        <svg
                          className={`branchCardEditIcon${editingBranchId === branch.id ? " branchCardEditIconOpen" : ""}`}
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      <div
                        className={`branchCardBody${editingBranchId === branch.id ? " branchCardBodyOpen" : ""}`}
                      >
                        <div className="branchCardFields">
                          <div className="field">
                            <label>
                              Branch Name <span className="req">*</span>
                            </label>
                            <Input
                              type="text"
                              autoComplete="off"
                              name={`branchName_${idx}`}
                              value={branch.branchName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleBranchChange(
                                  idx,
                                  "branchName",
                                  e.target.value,
                                )
                              }
                              onBlur={() => handleBranchBlur(idx, "branchName")}
                              className={
                                branchTouched[idx]?.branchName &&
                                branchErrors[idx]?.branchName
                                  ? "inputError"
                                  : ""
                              }
                              placeholder="e.g. Downtown Branch"
                              maxLength={CHAR_LIMIT.NAME}
                              label={undefined}
                            />
                            <ErrorMessage
                              value={branch.branchName}
                              have={CHAR_LIMIT.NAME}
                              touched={branchTouched[idx]?.branchName}
                              error={branchErrors[idx]?.branchName}
                            />
                          </div>

                          <div className="field">
                            <label>
                              Branch Address <span className="req">*</span>
                            </label>
                            <Input
                              type="text"
                              autoComplete="off"
                              name={`branchAddress_${idx}`}
                              value={branch.branchAddress}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                handleBranchChange(
                                  idx,
                                  "branchAddress",
                                  e.target.value,
                                )
                              }
                              onBlur={() =>
                                handleBranchBlur(idx, "branchAddress")
                              }
                              className={
                                branchTouched[idx]?.branchAddress &&
                                branchErrors[idx]?.branchAddress
                                  ? "inputError"
                                  : ""
                              }
                              placeholder="e.g. 100 Searsport Avenue"
                              maxLength={CHAR_LIMIT.ADDRESS}
                              label={undefined}
                            />
                            <ErrorMessage
                              value={branch.branchAddress}
                              have={CHAR_LIMIT.ADDRESS}
                              touched={branchTouched[idx]?.branchAddress}
                              error={branchErrors[idx]?.branchAddress}
                            />
                          </div>

                          <div className="branchCardActions">
                            <Button
                              className="btn btnSecondary"
                              type="button"
                              onClick={() => cancelBranchEdit(idx)}
                              disabled={branchUpdateLoading}
                            >
                              Cancel
                            </Button>
                            <Button
                              className="btn btnPrimary"
                              type="button"
                              onClick={() => handleUpdateBranch(idx)}
                              disabled={branchUpdateLoading}
                            >
                              {branchUpdateLoading ? (
                                <>
                                  <span className="btnSpinner" /> Updating
                                </>
                              ) : (
                                "Update"
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : drawerMode !== "view" ? (
                    /* New branch row (new-bank create flow only) */
                    <div className="branchFormRow">
                      <div className="branchRowHeader">
                        <span className="branchRowLabel">New Branch</span>
                        <Button
                          className="btn btnGhost btnDanger"
                          type="button"
                          onClick={() => removeBranch(idx)}
                        >
                          Remove
                        </Button>
                      </div>

                      <div className="fieldRow">
                        <div className="field">
                          <label>
                            Branch Name <span className="req">*</span>
                          </label>
                          <Input
                            type="text"
                            autoComplete="off"
                            name={`branchName_${idx}`}
                            value={branch.branchName}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              handleBranchChange(
                                idx,
                                "branchName",
                                e.target.value,
                              )
                            }
                            onBlur={() => handleBranchBlur(idx, "branchName")}
                            className={
                              branchTouched[idx]?.branchName &&
                              branchErrors[idx]?.branchName
                                ? "inputError"
                                : ""
                            }
                            placeholder="e.g. Downtown Branch"
                            maxLength={CHAR_LIMIT.NAME}
                            label={undefined}
                          />
                          <ErrorMessage
                            value={branch.branchName}
                            have={CHAR_LIMIT.NAME}
                            touched={branchTouched[idx]?.branchName}
                            error={branchErrors[idx]?.branchName}
                          />
                        </div>
                      </div>

                      <div className="fieldRow">
                        <div className="field">
                          <label>
                            Branch Address <span className="req">*</span>
                          </label>
                          <Input
                            type="text"
                            autoComplete="off"
                            name={`branchAddress_${idx}`}
                            value={branch.branchAddress}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              handleBranchChange(
                                idx,
                                "branchAddress",
                                e.target.value,
                              )
                            }
                            onBlur={() =>
                              handleBranchBlur(idx, "branchAddress")
                            }
                            className={
                              branchTouched[idx]?.branchAddress &&
                              branchErrors[idx]?.branchAddress
                                ? "inputError"
                                : ""
                            }
                            placeholder="e.g. 100 Searsport Avenue"
                            maxLength={CHAR_LIMIT.ADDRESS}
                            label={undefined}
                          />
                          <ErrorMessage
                            value={branch.branchAddress}
                            have={CHAR_LIMIT.ADDRESS}
                            touched={branchTouched[idx]?.branchAddress}
                            error={branchErrors[idx]?.branchAddress}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        )}

        {/* Add Branch form (addBranch mode) */}
        {drawerMode === "addBranch" && (
          <div className="formSection">
            <div className="formSectionTitle">Branch Details</div>

            <div className="fieldRow">
              <div className="field">
                <label>
                  Branch Name <span className="req">*</span>
                </label>
                <Input
                  type="text"
                  autoComplete="off"
                  name="newBranchName"
                  value={branches[0]?.branchName ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleBranchChange(0, "branchName", e.target.value)
                  }
                  onBlur={() => handleBranchBlur(0, "branchName")}
                  className={
                    branchTouched[0]?.branchName && branchErrors[0]?.branchName
                      ? "inputError"
                      : ""
                  }
                  placeholder="e.g. Downtown Branch"
                  maxLength={CHAR_LIMIT.NAME}
                  label={undefined}
                />
                <ErrorMessage
                  value={branches[0]?.branchName ?? ""}
                  have={CHAR_LIMIT.NAME}
                  touched={branchTouched[0]?.branchName}
                  error={branchErrors[0]?.branchName}
                />
              </div>
            </div>

            <div className="fieldRow">
              <div className="field">
                <label>
                  Branch Address <span className="req">*</span>
                </label>
                <Input
                  type="text"
                  autoComplete="off"
                  name="newBranchAddress"
                  value={branches[0]?.branchAddress ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleBranchChange(0, "branchAddress", e.target.value)
                  }
                  onBlur={() => handleBranchBlur(0, "branchAddress")}
                  className={
                    branchTouched[0]?.branchAddress &&
                    branchErrors[0]?.branchAddress
                      ? "inputError"
                      : ""
                  }
                  placeholder="e.g. 100 Searsport Avenue"
                  maxLength={CHAR_LIMIT.ADDRESS}
                  label={undefined}
                />
                <ErrorMessage
                  value={branches[0]?.branchAddress ?? ""}
                  have={CHAR_LIMIT.ADDRESS}
                  touched={branchTouched[0]?.branchAddress}
                  error={branchErrors[0]?.branchAddress}
                />
              </div>
            </div>
          </div>
        )}
      </Offcanvas>
    </div>
  );
}
